// Several Radix primitives (Select's focusSelectedItem/roving tabindex,
// Dialog's initial-focus fallback paths, etc.) call `element.scrollIntoView()`
// and/or `element.focus()` on elements that may be off-screen, without a
// `preventScroll` guard on every call site. `.focus()` on its own auto-scrolls
// an off-screen element into view by default (plain DOM behavior, independent
// of any scrollIntoView call around it). When the triggering element sits
// below the fold, this is what reads as "the page jumps to the top" on open.
// Radix exposes no prop to opt out of this on most primitives, so both DOM
// APIs are neutralized for the primitive's entire open→close lifecycle
// instead — call this on open, call the returned restorer on close.
export function suppressScrollAdjustments(): () => void {
  const proto = HTMLElement.prototype;
  const originalScrollIntoView = proto.scrollIntoView;
  const originalFocus = proto.focus;
  proto.scrollIntoView = function () {};
  proto.focus = function (options?: FocusOptions) {
    originalFocus.call(this, { ...options, preventScroll: true });
  };
  return () => {
    proto.scrollIntoView = originalScrollIntoView;
    proto.focus = originalFocus;
  };
}

// Belt-and-suspenders for the same jump: react-remove-scroll (which every
// Dialog/Sheet/Popover in packages/ui pulls in for its background-scroll
// lock) sets `body[data-scroll-locked] { overflow: hidden !important }`.
// Measured live (scripted repro, DevTools instrumentation): the instant
// that attribute lands, `window.scrollY` is already clamped to 0 — even
// though nothing in this app calls `scrollTo`/`focus`/`scrollIntoView`, and
// even though body's rendered height has already recovered to its full
// content extent by the very next frame. This is the browser's own
// engine-internal scroll-offset clamp reacting to a transient reflow that
// happens faster than any JS-observable event fires, so it can't be
// prevented — only corrected after the fact.
//
// The offset has to be captured by the *caller*, during render, not in here:
// react-remove-scroll's own lock effect lives on a descendant
// (DialogContent → RemoveScroll), and React fires child layout effects
// before parent ones in the same commit — so by the time a parent-level
// `useLayoutEffect` in Dialog/AlertDialog could call this, the clamp has
// already happened and `window.scrollY` is already 0. Capturing during
// render (before any effect in the commit runs at all) is the only point
// that's guaranteed to run first; pass that snapshot in here.
export function restoreScrollPositionAfterLock(snapshot: { x: number; y: number }): () => void {
  const { x, y } = snapshot;
  let rafId = requestAnimationFrame(() => {
    rafId = requestAnimationFrame(() => {
      if (window.scrollX !== x || window.scrollY !== y) {
        window.scrollTo(x, y);
      }
    });
  });
  return () => cancelAnimationFrame(rafId);
}
